// THE FILL STONE (owner 2026-10-16: "for the irregular stone walls vs cube
// stones: they could be procedurally made to fit the area that they have to
// fit, and be made look irregular, this would work everywhere ... they should
// get the same filling stones"): ONE procedural stone on every lit triangle of
// the materials STONE_MATS — masonry (retaining walls, fill walls, path holding
// walls, deck bank walls) and stone (abutment blocks, approach / ramp blocks,
// portals). Computed in the lit program's fragment shader (structs2.js
// nosings) in world metres: no texture lookup but the shared speckle grain.
//
// FITTED: a steep stone triangle's corners carry (v, H) in the lit batch's
// existing uv attribute (0 extra bytes per vertex): v = the corner's height
// over the foot of its COLUMN, H = the column's height (wallCoords below, at
// mesh build). The fragment lays N = courses(H) courses between the foot and
// the top — a whole number, per fragment, so a sloped top keeps whole courses
// and the pattern is continuous across records (a seam column carries the same
// (v, H) on both sides); where N steps along a face the step line is a head
// joint. The top course is the cap: longer, flatter stones. A flat stone
// triangle (uv 0, 0) gets a plan view of cap stones, never a stretched wall.
//
// IRREGULAR: course beds wander (value noise in plan, the foot and the top
// fixed), stones within a course are the cells of a plan-xz Voronoi (seeds per
// course) sliced by the face — world-anchored, so continuous across kinks,
// segments and chunks without an arc length — their head joints leaning per
// course; rounded corners, lighter mortar joints (the owner's photo,
// OWNER_RULES 2026-10-13), a per-stone tone in the Engadin grey-brown /
// grey-green gneiss range around the masonry albedo (the drawn mean), an edge
// shade and a fake relief: the normal tilts toward each joint and per stone.
// Beyond fade_m (or once a pixel spans fade_px_m) the pattern fades to its
// mean: no shimmer in VR, no cost to the eye. Hash cells, a 3 x 3 Voronoi in
// two passes, no loop over many cells.
import { MATS } from '../../game/structures.js';
import { MAT_DRAW } from './structplan.js';

// the materials drawn as the fill stone (lit batch, not a stair, not a light strip)
export const STONE_MATS = ['masonry', 'stone'];
export const STONE_MAT_IDX = new Set(STONE_MATS.map((m) => {
  const i = MATS.indexOf(m);
  if (i < 0) throw new Error(`stonewall: STONE_MATS "${m}" is no structure material (game/structures.js MATS)`);
  return i;
}));

// the stone's shape (metres unless noted), each with its reason
export const SW = {
  course_m: [0.22, 0.04, 0.6],   // course height C(H) = min(0.6, 0.22 + 0.04 H): a 0.4 m path wall 2 courses of 0.2 m, a 6 m wall 13 of 0.46 m — bigger walls, bigger stones
  steep_ny: 0.6,                 // |n.y| under this: a wall face (fitted courses); else a top (plan cap stones)
  min_h_m: 0.02,                 // a column shorter than this is no column (the record's extent stands in)
  bed_wave: 0.36,                // a bed joint wanders +-0.18 of a course (course heights 0.64 .. 1.36 of the mean)
  bed_wave_m: 5.0,               // ... over this plan period
  stone_m: 0.7,                  // Voronoi cell of a wall course: stones ~0.3 .. 1.1 m long
  cap_m: 1.1,                    // the cap course's cell: longer flat stones
  plan_m: 0.8,                   // a top's cap-stone cell
  lean: 1.6,                     // head joints lean up to +-0.8 of their course height (+-39 deg)
  lean_m: 2.7,                   // ... changing over this plan period
  joint_m: 0.011,                // half a joint: 2.2 cm joints
  round_m: 0.07,                 // corner rounding (at most 0.35 of a course)
  bevel: 0.55, bevel_m: 0.05,    // the relief: normal tilt toward a joint, over this width
  tilt: 0.12,                    // a stone's own face tilt (each catches the sun differently)
  edge_ao: 0.16, edge_ao_m: 0.06,  // the shade along a stone's edge
  grain: 0.6,                    // share of the speckle grain inside a stone
  tone: [0.78, 0.44],            // stone lightness 0.78 .. 1.22 (mean 1)
  warm: [1.06, 1.0, 0.9], cool: [0.95, 1.0, 1.06],   // brown-grey .. grey-green gneiss
  mortar: [1.3, 0.98, 0.93],     // mortar (lighter joints): 1.3 x the stone's mean luminance, green 0.98 / blue 0.93 of red: a warm light grey
  fade_m: [70, 110],             // the pattern fades to the mean over this distance ...
  fade_px_m: [0.1, 0.2],         // ... or once a pixel spans this much wall
};

// the course count of a face H metres high: a whole number from the foot to the top
export function courses(H) {
  const [c0, c1, cmax] = SW.course_m;
  return Math.max(1, Math.floor(H / Math.min(cmax, c0 + c1 * H) + 0.5));
}

const lin = (v) => (v <= 0.04045 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4);
// the stone's mean = the masonry albedo (client/materials/struct manifest albedo_hex), linear
export const STONE_LIN = [16, 8, 0].map((s) => lin(((MAT_DRAW.masonry.color >> s) & 255) / 255));
// the lit batch's vertex colour of a stone triangle: the mean as linear bytes (the pattern's mean is 1)
export const STONE_RGB = STONE_LIN.map((v) => Math.min(255, Math.round(v * 255)));

// a triangle's |n.y| < steep_ny (a wall face)
export function steepTri(verts, idx, t) {
  const a = idx[t * 3] * 3, b = idx[t * 3 + 1] * 3, c = idx[t * 3 + 2] * 3;
  const ux = verts[b] - verts[a], uy = verts[b + 1] - verts[a + 1], uz = verts[b + 2] - verts[a + 2];
  const vx = verts[c] - verts[a], vy = verts[c + 1] - verts[a + 1], vz = verts[c + 2] - verts[a + 2];
  const nx = uy * vz - uz * vy, ny = uz * vx - ux * vz, nz = ux * vy - uy * vx;
  return Math.abs(ny) < SW.steep_ny * (Math.hypot(nx, ny, nz) || 1);
}

// THE FITTED FACE of record s: per record vertex (v, H) for the vertices of
// its steep stone triangles (isStone(t) && steepTri), NaN elsewhere. A column
// = those vertices at the same x, z (the emitters' wall columns: foot, top and
// any vertex between); a vertex alone at its x, z (an arch, a battered face)
// takes the record's steep stone extent.
export function wallCoords(str, s, isStone) {
  const { verts, idx } = str;
  const v0 = str.firstVert[s], nv = str.nVert[s];
  const out = new Float32Array(nv * 2).fill(NaN);
  const col = new Map(), keyOf = new Array(nv);
  let ylo = Infinity, yhi = -Infinity;
  for (let t = str.firstTri[s]; t < str.firstTri[s] + str.nTri[s]; t++) {
    if (!isStone(t) || !steepTri(verts, idx, t)) continue;
    for (let k = 0; k < 3; k++) {
      const v = idx[t * 3 + k], y = verts[v * 3 + 1], key = verts[v * 3] + ',' + verts[v * 3 + 2];
      keyOf[v - v0] = key;
      const c = col.get(key);
      if (!c) col.set(key, [y, y]);
      else { if (y < c[0]) c[0] = y; if (y > c[1]) c[1] = y; }
      if (y < ylo) ylo = y;
      if (y > yhi) yhi = y;
    }
  }
  for (let i = 0; i < nv; i++) {
    if (keyOf[i] === undefined) continue;
    let [lo, hi] = col.get(keyOf[i]);
    if (hi - lo < SW.min_h_m) { lo = ylo; hi = yhi; }
    out[i * 2] = verts[(v0 + i) * 3 + 1] - lo;
    out[i * 2 + 1] = hi - lo;
  }
  return out;
}

const f = (v) => {
  const s = (+v).toFixed(5);
  return s.includes('.') ? s : s + '.0';
};
const v3 = (a) => `vec3(${a.map(f).join(', ')})`;

// GLSL at <common> of the lit fragment shader (needs varyings vWp, vWn and the
// speckle sampler's uv vMapUv, declared by the caller)
export function stonePars() {
  const [c0, c1, cmax] = SW.course_m;
  const Lm = 0.2126 * STONE_LIN[0] + 0.7152 * STONE_LIN[1] + 0.0722 * STONE_LIN[2];
  const mortar = STONE_LIN.map((v, c) => (SW.mortar[0] * Lm * (c ? SW.mortar[c] : 1)) / v);
  return `
float swH1(vec3 p) { p = fract(p * 0.1031); p += dot(p, p.zyx + 31.32); return fract((p.x + p.y) * p.z); }
vec2 swH2(vec3 p) { p = fract(p * vec3(0.1031, 0.1030, 0.0973)); p += dot(p, p.yzx + 33.33); return fract((p.xx + p.yz) * p.zy); }
float swNoise(vec2 x, float k) {
  vec2 i = floor(x), u = fract(x);
  u = u * u * (3.0 - 2.0 * u);
  return mix(mix(swH1(vec3(i, k)), swH1(vec3(i + vec2(1.0, 0.0), k)), u.x),
    mix(swH1(vec3(i + vec2(0.0, 1.0), k)), swH1(vec3(i + vec2(1.0, 1.0), k)), u.x), u.y);
}
// courses before rounding of a face H high (N = floor(F + 0.5), at least 1)
float swCourses(float H) { return H / min(${f(cmax)}, ${f(c0)} + ${f(c1)} * H); }
// bed joint k of N, in course units: the foot (0) and the top (N) fixed, the rest wander in plan
float swBound(float k, float N, vec2 xz) {
  return (k <= 0.0 || k >= N) ? clamp(k, 0.0, N) : k + ${f(SW.bed_wave)} * (swNoise(xz * ${f(1 / SW.bed_wave_m)}, k) - 0.5);
}
// the stone at plan point q in course c (Voronoi cell S): x = its hash, y = the distance to its edge
// (along T on a wall face, in plan on a top), zw = the plan direction toward that edge
vec4 swCell(vec2 q, float S, float c, vec2 T, float along) {
  vec2 g = floor(q / S), s1 = vec2(0.0), c1 = g;
  float best = 1e9;
  for (int j = -1; j <= 1; j++) for (int i = -1; i <= 1; i++) {
    vec2 cell = g + vec2(float(i), float(j));
    vec2 s = (cell + 0.15 + 0.7 * swH2(vec3(cell, c))) * S;
    float dd = dot(s - q, s - q);
    if (dd < best) { best = dd; s1 = s; c1 = cell; }
  }
  float dmin = 1e9;
  vec2 dir = vec2(0.0);
  for (int j = -1; j <= 1; j++) for (int i = -1; i <= 1; i++) {
    if (i == 0 && j == 0) continue;
    vec2 cell = c1 + vec2(float(i), float(j));
    vec2 s = (cell + 0.15 + 0.7 * swH2(vec3(cell, c))) * S;
    vec2 e = normalize(s - s1);
    float d = dot((s1 + s) * 0.5 - q, e);
    float a = abs(dot(e, T));
    d = along > 0.5 ? (a > 1e-3 ? d / a : 1e9) : d;
    if (d < dmin) { dmin = d; dir = e; }
  }
  return vec4(swH1(vec3(c1, c + 71.0)), dmin, dir);
}
// distance to a stone's edge with rounded corners (negative: the corner's mortar)
float swRound(float a, float b, float r) { return (a < r && b < r) ? r - length(vec2(r - a, r - b)) : min(a, b); }
// THE STONE at world p: vh = (v, H) (H 0: a top), F = swCourses(H), ds = metres along the face per unit
// of F, fp = the pixel's footprint (m), grain = the speckle over its mean, fade 1 near .. 0 far; tilt
// (world) out: the relief. Returns the colour factor over the mean.
vec3 swStone(vec3 p, vec2 T, vec2 vh, float F, float ds, float fp, vec3 grain, float fade, out vec3 tilt) {
  float dEdge, id, cap, r;
  vec3 tb, Tw = vec3(T.x, 0.0, T.y);
  if (vh.y > ${f(SW.min_h_m)}) {
    float N = max(1.0, floor(F + 0.5)), h = vh.y / N;
    float t = clamp(vh.x / h, 0.0, N);
    float c = min(floor(t), N - 1.0);
    float lo = swBound(c, N, p.xz), hi = swBound(c + 1.0, N, p.xz);
    if (t < lo) { c -= 1.0; hi = lo; lo = swBound(c, N, p.xz); }
    else if (t >= hi && c + 1.0 < N) { c += 1.0; lo = hi; hi = swBound(c + 1.0, N, p.xz); }
    cap = step(N - 1.5, c);
    float lean = ${f(SW.lean)} * (swNoise(p.xz * ${f(1 / SW.lean_m)}, c + 13.0) - 0.5);
    vec2 q = p.xz + T * (lean * ((t - lo) / max(hi - lo, 1e-3) - 0.5) * (hi - lo) * h);
    vec4 cell = swCell(q, mix(${f(SW.stone_m)}, ${f(SW.cap_m)}, cap), c, T, 1.0);
    float dLo = c > 0.5 ? (t - lo) * h : 1e3, dHi = (hi - t) * h;   // no bed at the foot
    float g = F - 0.5, k = floor(g + 0.5);
    float dJ = k >= 1.0 ? abs(g - k) * ds : 1e3;                     // where the course count steps
    float dBed = min(dLo, dHi), dHead = min(cell.y, dJ);
    r = min(${f(SW.round_m)}, 0.35 * (hi - lo) * h);
    dEdge = swRound(dBed, dHead, r);
    id = cell.x;
    tb = dBed < dHead ? vec3(0.0, dLo < dHi ? -1.0 : 1.0, 0.0) : (cell.y < dJ ? Tw * sign(dot(cell.zw, T)) : vec3(0.0));
  } else {
    vec4 cell = swCell(p.xz, ${f(SW.plan_m)}, -5.0, T, 0.0);
    r = ${f(SW.round_m)};
    dEdge = cell.y;
    id = cell.x;
    cap = 1.0;
    tb = vec3(cell.z, 0.0, cell.w);
  }
  float jw = ${f(SW.joint_m)};
  // the mortar's share of this pixel: its footprint (box) under the joint line dEdge < jw (a corner's
  // negative distance is mortar too)
  float cov = clamp((jw - dEdge) / max(fp, 1e-4) + 0.5, 0.0, 1.0);
  float w = fract(id * 37.719 + 0.213), w2 = fract(id * 91.31 + 0.57);
  vec3 tone = (${f(SW.tone[0])} + ${f(SW.tone[1])} * id) * mix(mix(${v3(SW.warm)}, ${v3(SW.cool)}, w), vec3(1.0), 0.3 * cap);
  tone *= mix(vec3(1.0), grain, ${f(SW.grain)});
  tone *= 1.0 - ${f(SW.edge_ao)} * (1.0 - smoothstep(jw, jw + ${f(SW.edge_ao_m)}, dEdge));
  vec3 col = mix(tone, ${v3(mortar)}, cov);
  tilt = fade * (tb * ${f(SW.bevel)} * (1.0 - smoothstep(jw, jw + ${f(SW.bevel_m)}, dEdge))
    + ${f(SW.tilt)} * ((w - 0.5) * Tw + (w2 - 0.5) * vec3(0.0, 1.0, 0.0) + (w2 - 0.5) * cap * vec3(T.y, 0.0, -T.x)));
  return mix(vec3(1.0), col, fade);
}
`;
}

// GLSL in place of <map_fragment> (inside USE_MAP; isStone: the stair-code test of a stone triangle;
// grainMean: the speckle's linear mean; dm: its box-map repeat, metres). Declares swTilt for stoneNormal.
export function stoneMap(isStone, grainMean, dm) {
  return `vec3 swTilt = vec3(0.0);
      {
        float swIs = ${isStone};
        vec3 swN = normalize(vWn);
        vec2 swT = normalize(vec2(-swN.z, swN.x) + 1e-6);
        float swS = dot(vWp.xz, swT);
        // derivatives in uniform control flow: the grain, the footprint, the course count's gradient
        vec2 swG = (vMapUv.y > ${f(SW.min_h_m)} ? vec2(swS, vWp.y) : vWp.xz) / ${f(dm)};
        vec4 swGrain = texture2D(map, mix(vMapUv, swG, swIs));
        float swFp = max(length(dFdx(vWp)), length(dFdy(vWp)));
        float swF = swCourses(max(vMapUv.y, 1e-3));
        float swDs = length(vec2(dFdx(swS), dFdy(swS))) / max(length(vec2(dFdx(swF), dFdy(swF))), 1e-6);
        if (swIs > 0.5) {
          float swFade = 1.0 - max(smoothstep(${f(SW.fade_m[0])}, ${f(SW.fade_m[1])}, length(vViewPosition)), smoothstep(${f(SW.fade_px_m[0])}, ${f(SW.fade_px_m[1])}, swFp));
          diffuseColor.rgb *= swStone(vWp, swT, vMapUv, swF, swDs, swFp, swGrain.rgb / ${f(grainMean)}, swFade, swTilt);
        } else {
          diffuseColor *= swGrain;
        }
      }`;
}

// GLSL after <normal_fragment_maps>: the relief (world tilt -> view space)
export const STONE_NORMAL = 'normal = normalize(normal + mat3(viewMatrix) * swTilt);';
